import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import IncomeSection from './IncomeSection';
import TaxStatusSection from './TaxStatusSection';
import ExpensesSection from './ExpensesSection';
import DeductionsSection from './DeductionsSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета налогов для самозанятых
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  periodTypes,
  fiscalRoles,
  taxSystems,
  russianRegions,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev };
      
      // Обработка вложенных полей для customPeriod
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        newState[parent] = { ...prev[parent], [child]: fieldValue };
      } else {
        newState[name] = fieldValue;
      }

      // Логика автоматической корректировки зависимых полей
      if (name === 'fiscalRole') {
        // Если выбран ИП, автоматически устанавливаем флаг
        if (fieldValue === 'entrepreneur') {
          newState.hasActiveIP = true;
        }
      }
      
      // Если изменился тип налогообложения
      if (name === 'taxSystem') {
        // Для НПД у ИП ставка с физлиц 6%, с юрлиц 4%
        if (fieldValue === 'npd' && newState.fiscalRole === 'entrepreneur') {
          newState.incomeType = 'ip_insurance';
        } else if (fieldValue === 'npd' && newState.fiscalRole === 'individual') {
          newState.incomeType = 'general';
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'hasExpenses' && !checked) {
        newState.expenses = '0';
        newState.expenseItems = [];
      }
      
      if (name === 'hasDeductions' && !checked) {
        newState.deductions = '0';
        newState.hasChildrenDeduction = false;
        newState.childrenCount = 0;
      }
      
      if (name === 'hasChildrenDeduction' && !checked) {
        newState.childrenCount = 0;
      }
      
      if (name === 'includeInsurance' && !checked) {
        newState.insuranceAmount = '0';
      }
      
      if (name === 'hasOtherIncome' && !checked) {
        newState.otherIncome = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      incomeAmount, expenses, deductions, insuranceAmount, otherIncome,
      incomePeriod, customPeriod, fiscalRole, taxSystem
    } = formData;

    // Парсинг числовых значений
    const numIncomeAmount = parseFloatOrDefault(incomeAmount, -1);
    const numExpenses = parseFloatOrDefault(expenses, 0);
    const numDeductions = parseFloatOrDefault(deductions, 0);
    const numInsuranceAmount = parseFloatOrDefault(insuranceAmount, 0);
    const numOtherIncome = parseFloatOrDefault(otherIncome, 0);

    // Валидация базовых полей
    if (numIncomeAmount <= 0) newErrors.incomeAmount = 'Сумма дохода должна быть положительной';
    if (!newErrors.incomeAmount && numIncomeAmount > 5000000000) {
      newErrors.incomeAmount = 'Максимальная сумма 5 млрд ₽';
    }
    
    // Расходы не могут быть отрицательными
    if (formData.hasExpenses && numExpenses < 0) {
      newErrors.expenses = 'Расходы не могут быть отрицательными';
    }
    
    // Расходы не могут быть больше дохода для УСН доходы-расходы
    if (formData.hasExpenses && taxSystem === 'usn_profit' && numExpenses > numIncomeAmount) {
      newErrors.expenses = 'Расходы не могут превышать доходы';
    }
    
    // Вычеты не могут быть отрицательными
    if (formData.hasDeductions && numDeductions < 0) {
      newErrors.deductions = 'Вычеты не могут быть отрицательными';
    }
    
    // Страховые взносы не могут быть отрицательными
    if (formData.includeInsurance && numInsuranceAmount < 0) {
      newErrors.insuranceAmount = 'Страховые взносы не могут быть отрицательными';
    }
    
    // Прочие доходы не могут быть отрицательными
    if (formData.hasOtherIncome && numOtherIncome < 0) {
      newErrors.otherIncome = 'Прочие доходы не могут быть отрицательными';
    }
    
    // Валидация периода
    if (incomePeriod === 'custom') {
      try {
        const startDate = new Date(customPeriod.startDate);
        const endDate = new Date(customPeriod.endDate);
        
        if (isNaN(startDate.getTime())) {
          newErrors['customPeriod.startDate'] = 'Некорректная дата начала';
        }
        
        if (isNaN(endDate.getTime())) {
          newErrors['customPeriod.endDate'] = 'Некорректная дата окончания';
        }
        
        if (!newErrors['customPeriod.startDate'] && !newErrors['customPeriod.endDate'] && startDate >= endDate) {
          newErrors['customPeriod.endDate'] = 'Дата окончания должна быть позже даты начала';
        }
      } catch (e) {
        newErrors['customPeriod.startDate'] = 'Ошибка в датах';
        newErrors['customPeriod.endDate'] = 'Ошибка в датах';
      }
    }

    // Для УСН проверяем, не превышает ли доход лимит
    if (taxSystem === 'usn_income' || taxSystem === 'usn_profit') {
      let annualIncomeEstimate = numIncomeAmount;
      
      // Пересчитываем в годовой доход в зависимости от периода
      if (incomePeriod === 'month') {
        annualIncomeEstimate *= 12;
      } else if (incomePeriod === 'quarter') {
        annualIncomeEstimate *= 4;
      }
      
      if (annualIncomeEstimate > 219200000) { // Лимит на 2023 год
        newErrors.taxSystem = 'Превышен лимит дохода для УСН (219,2 млн ₽ в год)';
      }
    }

    // Для НПД проверяем, не превышает ли доход лимит
    if (taxSystem === 'npd') {
      let annualIncomeEstimate = numIncomeAmount;
      
      // Пересчитываем в годовой доход в зависимости от периода
      if (incomePeriod === 'month') {
        annualIncomeEstimate *= 12;
      } else if (incomePeriod === 'quarter') {
        annualIncomeEstimate *= 4;
      }
      
      if (annualIncomeEstimate > 2400000) { // Лимит для НПД
        newErrors.taxSystem = 'Превышен лимит дохода для НПД (2,4 млн ₽ в год)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета налогов для самозанятых
  const calculateTax = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numIncomeAmount = parseFloatOrDefault(formData.incomeAmount);

        if (numIncomeAmount <= 0) {
          setErrors({ general: "Сумма дохода должна быть положительной." });
          setIsCalculating(false);
          return;
        }

        const incomeType = formData.incomeType;
        const incomePeriod = formData.incomePeriod;
        const taxSystem = formData.taxSystem;
        
        // Расчет налоговой ставки в зависимости от типа дохода и системы налогообложения
        let taxRate = 0;
        if (taxSystem === 'npd') {
          // НПД - Налог на профессиональный доход (самозанятые)
          taxRate = incomeType === 'general' ? 0.04 : 0.06; // 4% с физлиц, 6% с юрлиц
        } else if (taxSystem === 'usn_income') {
          // УСН Доходы
          taxRate = 0.06; // 6%
        } else if (taxSystem === 'usn_profit') {
          // УСН Доходы минус расходы
          taxRate = 0.15; // 15%
        }

        // Расчет расходов (для УСН доходы минус расходы)
        const numExpenses = formData.hasExpenses ? parseFloatOrDefault(formData.expenses, 0) : 0;
        
        // Расчет вычетов
        const numDeductions = formData.hasDeductions ? parseFloatOrDefault(formData.deductions, 0) : 0;
        
        // Расчет бонусного вычета (до 10,000 руб) для НПД
        let npdBonusDeduction = 0;
        if (taxSystem === 'npd') {
          npdBonusDeduction = Math.min(10000, numIncomeAmount * taxRate * 0.5);
        }
        
        // Расчет страховых взносов для ИП
        const hasActiveIP = formData.hasActiveIP || formData.fiscalRole === 'entrepreneur';
        const fixedInsuranceFee = hasActiveIP ? 45842 : 0; // Фиксированные взносы ИП за 2023 год
        
        // Добровольные взносы на пенсионное страхование
        const voluntaryInsurance = formData.includeInsurance 
          ? parseFloatOrDefault(formData.insuranceAmount, 0) 
          : 0;
        
        // Расчет общих страховых взносов
        const totalInsurance = fixedInsuranceFee + voluntaryInsurance;

        // --- Расчет налоговой базы и налога ---
        let taxableIncome = numIncomeAmount;
        
        // Для УСН доходы-расходы вычитаем расходы
        if (taxSystem === 'usn_profit') {
          taxableIncome = Math.max(0, numIncomeAmount - numExpenses);
        }
        
        // Для УСН можно вычесть страховые взносы
        let taxDeduction = 0;
        if ((taxSystem === 'usn_income' || taxSystem === 'usn_profit') && hasActiveIP) {
          // Для УСН Доходы можно уменьшить налог на взносы, но не более чем на 50%
          if (taxSystem === 'usn_income') {
            taxDeduction = Math.min(taxableIncome * taxRate * 0.5, totalInsurance);
          } else {
            // Для УСН Доходы-Расходы взносы учитываются в расходах
            taxableIncome = Math.max(0, taxableIncome - totalInsurance);
          }
        } else if (taxSystem === 'npd') {
          // Для НПД применяем бонусный вычет
          taxDeduction = npdBonusDeduction;
        }

        // Расчет налога
        let calculatedTax = taxableIncome * taxRate;
        
        // Для НПД вычитаем бонусный вычет (уже в процентах)
        if (taxSystem === 'npd') {
          calculatedTax = Math.max(0, calculatedTax - taxDeduction);
        } else if (taxSystem === 'usn_income') {
          // Для УСН Доходы вычитаем сумму страховых взносов
          calculatedTax = Math.max(0, calculatedTax - taxDeduction);
        }
        
        // Минимальный налог для УСН доходы-расходы (1% от дохода)
        let minTax = 0;
        if (taxSystem === 'usn_profit') {
          minTax = numIncomeAmount * 0.01;
          calculatedTax = Math.max(calculatedTax, minTax);
        }

        // --- Подготовка данных для расчета помесячного графика платежей ---
        let monthlySchedule = [];
        let currentYear = new Date().getFullYear();
        let isMinTaxApplied = false;
        
        // Определяем период для распределения доходов
        let periodMonths = 1;
        if (incomePeriod === 'month') {
          periodMonths = 1;
        } else if (incomePeriod === 'quarter') {
          periodMonths = 3;
        } else if (incomePeriod === 'year') {
          periodMonths = 12;
        } else if (incomePeriod === 'custom') {
          const startDate = new Date(formData.customPeriod.startDate);
          const endDate = new Date(formData.customPeriod.endDate);
          periodMonths = Math.ceil((endDate - startDate) / (30 * 24 * 60 * 60 * 1000));
        }
        
        // Расчет ежемесячного дохода
        const monthlyIncome = numIncomeAmount / periodMonths;
        
        // Расчет общей налоговой нагрузки
        const totalTaxBurden = calculatedTax + (hasActiveIP ? fixedInsuranceFee : 0);
        const effectiveTaxRate = (totalTaxBurden / numIncomeAmount) * 100;
        
        // Создаем график платежей помесячно
        for (let i = 0; i < periodMonths; i++) {
          const currentMonth = (new Date().getMonth() + i) % 12 + 1;
          const periodYear = currentYear + Math.floor((new Date().getMonth() + i) / 12);
          
          let monthTaxableIncome = monthlyIncome;
          let monthExpenses = numExpenses / periodMonths;
          
          // Для УСН доходы-расходы
          if (taxSystem === 'usn_profit') {
            monthTaxableIncome = Math.max(0, monthlyIncome - monthExpenses);
          }
          
          // Расчет страховых взносов для этого месяца
          let monthInsurance = 0;
          if (hasActiveIP) {
            // Распределяем фиксированные взносы равномерно
            monthInsurance = fixedInsuranceFee / 12;
            
            // Добавляем 1% от дохода свыше 300,000 руб (расчет по итогам года)
            if (currentMonth === 12 && numIncomeAmount > 300000) {
              const additionalFee = Math.min((numIncomeAmount - 300000) * 0.01, 182168); // максимум 1% от дохода
              monthInsurance += additionalFee;
            }
          }
          
          // Расчет налога за месяц
          let monthTax = 0;
          let monthDeduction = 0;
          
          if (taxSystem === 'npd') {
            monthTax = monthTaxableIncome * taxRate;
            // Бонусный вычет для НПД (распределяем равномерно)
            monthDeduction = npdBonusDeduction / periodMonths;
            monthTax = Math.max(0, monthTax - monthDeduction);
          } else if (taxSystem === 'usn_income') {
            monthTax = monthTaxableIncome * taxRate;
            // Вычет страховых взносов для УСН Доходы (не более 50% от налога)
            monthDeduction = Math.min(monthTax * 0.5, monthInsurance);
            monthTax = Math.max(0, monthTax - monthDeduction);
          } else if (taxSystem === 'usn_profit') {
            monthTax = monthTaxableIncome * taxRate;
            // Для УСН Доходы-Расходы проверяем минимальный налог в конце года
            if (currentMonth === 12) {
              const minMonthTax = monthlyIncome * 0.01;
              if (minMonthTax > monthTax) {
                monthTax = minMonthTax;
                isMinTaxApplied = true;
              }
            }
          }
          
          // Определяем сроки уплаты
          let paymentDue = false;
          let paymentDueDate = '';
          
          if (taxSystem === 'npd') {
            // Для НПД - до 25 числа следующего месяца
            paymentDue = true;
            const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
            const nextMonthYear = currentMonth === 12 ? periodYear + 1 : periodYear;
            paymentDueDate = `25.${String(nextMonth).padStart(2, '0')}.${nextMonthYear}`;
          } else if (taxSystem === 'usn_income' || taxSystem === 'usn_profit') {
            // Для УСН - авансовые платежи поквартально
            if (currentMonth === 3 || currentMonth === 6 || currentMonth === 9) {
              paymentDue = true;
              paymentDueDate = `25.${String(currentMonth + 1).padStart(2, '0')}.${periodYear}`;
            } else if (currentMonth === 12) {
              // Итоговый платеж по УСН
              paymentDue = true;
              paymentDueDate = `25.04.${periodYear + 1}`;
            }
          }
          
          // Добавляем запись в график
          monthlySchedule.push({
            month: currentMonth,
            year: periodYear,
            income: monthlyIncome,
            expenses: monthExpenses,
            taxableIncome: monthTaxableIncome,
            taxRate: taxRate * 100,
            taxAmount: monthTax,
            deduction: monthDeduction,
            insurance: monthInsurance,
            totalPayment: monthTax + monthInsurance,
            paymentDue: paymentDue,
            paymentDueDate: paymentDueDate,
            isMinTaxApplied: isMinTaxApplied && currentMonth === 12
          });
        }

        // --- Формирование итоговых результатов ---
        const finalResults = {
          incomeAmount: numIncomeAmount,
          incomeType: incomeType,
          taxSystem: taxSystem,
          taxRate: taxRate * 100,
          
          taxableIncome: taxableIncome,
          calculatedTax: calculatedTax,
          deduction: npdBonusDeduction + (taxSystem === 'usn_income' ? taxDeduction : 0),
          
          expenses: numExpenses,
          
          fixedInsuranceFee: fixedInsuranceFee,
          voluntaryInsurance: voluntaryInsurance,
          totalInsurance: totalInsurance,
          
          effectiveTaxRate: effectiveTaxRate,
          totalTaxBurden: totalTaxBurden,
          
          netIncome: numIncomeAmount - totalTaxBurden,
          incomePeriod: incomePeriod,
          periodMonths: periodMonths,
          
          isMinTaxApplied: isMinTaxApplied,
          minTax: minTax,
          
          schedule: monthlySchedule,
          
          // Сравнительная аналитика с другими системами налогообложения
          comparison: {
            npd: calculateComparisonTax('npd', numIncomeAmount, numExpenses, hasActiveIP),
            usn_income: calculateComparisonTax('usn_income', numIncomeAmount, numExpenses, hasActiveIP),
            usn_profit: calculateComparisonTax('usn_profit', numIncomeAmount, numExpenses, hasActiveIP)
          }
        };

        setResults(finalResults);
        setSchedule(monthlySchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating]);

  // Функция для расчета сравнительной информации по разным налоговым режимам
  const calculateComparisonTax = (system, income, expenses, isIP) => {
    let taxRate = 0;
    let taxAmount = 0;
    let deduction = 0;
    let taxableIncome = income;
    let insuranceFee = isIP ? 45842 : 0; // Фиксированные взносы ИП
    
    if (system === 'npd') {
      // НПД (самозанятость)
      taxRate = 4; // Базовая ставка 4% с физлиц
      taxAmount = income * 0.04;
      deduction = Math.min(10000, income * 0.04 * 0.5); // Налоговый вычет НПД
      taxAmount = Math.max(0, taxAmount - deduction);
      insuranceFee = 0; // Самозанятые не платят обязательные страховые взносы
    } else if (system === 'usn_income') {
      // УСН Доходы 6%
      taxRate = 6;
      taxAmount = income * 0.06;
      if (isIP) {
        // Уменьшение налога на страховые взносы, но не более 50%
        deduction = Math.min(taxAmount * 0.5, insuranceFee);
        taxAmount = Math.max(0, taxAmount - deduction);
      }
    } else if (system === 'usn_profit') {
      // УСН Доходы-Расходы 15%
      taxRate = 15;
      taxableIncome = Math.max(0, income - expenses);
      if (isIP) {
        // Страховые взносы учитываются в расходах
        taxableIncome = Math.max(0, taxableIncome - insuranceFee);
      }
      taxAmount = taxableIncome * 0.15;
      // Минимальный налог 1% от дохода
      const minTax = income * 0.01;
      taxAmount = Math.max(taxAmount, minTax);
    }
    
    return {
      taxRate,
      taxAmount,
      deduction,
      taxableIncome,
      insuranceFee,
      totalBurden: taxAmount + insuranceFee,
      effectiveRate: ((taxAmount + insuranceFee) / income) * 100
    };
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      incomeType: 'general',
      incomeAmount: '100000',
      incomeSource: 'russia',
      incomePeriod: 'month',
      customPeriod: {
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
      },
      hasExpenses: false,
      expenses: '0',
      expenseItems: [],
      hasDeductions: false,
      deductions: '0',
      hasChildrenDeduction: false,
      childrenCount: 0,
      monthlyIncome: [],
      incomeGrowthRate: 0,
      includeInsurance: false,
      insuranceAmount: '0',
      includeSocialBenefits: false,
      region: 'Moscow',
      hasOtherIncome: false,
      otherIncome: '0',
      fiscalRole: 'individual',
      hasActiveIP: false,
      ipRegistrationYear: new Date().getFullYear(),
      taxSystem: 'npd',
      adjustForInflation: false,
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика платежей в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = [
      'Месяц', 
      'Год', 
      'Доход', 
      'Расходы', 
      'Налогооблагаемая база', 
      'Ставка, %', 
      'Сумма налога', 
      'Вычет', 
      'Страховые взносы', 
      'Общий платеж',
      'Срок уплаты'
    ];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      return [
        item.month,
        item.year,
        formatCsvNumber(item.income),
        formatCsvNumber(item.expenses),
        formatCsvNumber(item.taxableIncome),
        item.taxRate,
        formatCsvNumber(item.taxAmount),
        formatCsvNumber(item.deduction),
        formatCsvNumber(item.insurance),
        formatCsvNumber(item.totalPayment),
        item.paymentDue ? item.paymentDueDate : '-'
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(results.incomeAmount),
      formatCsvNumber(results.expenses),
      formatCsvNumber(results.taxableIncome),
      results.taxRate,
      formatCsvNumber(results.calculatedTax),
      formatCsvNumber(results.deduction),
      formatCsvNumber(results.totalInsurance),
      formatCsvNumber(results.totalTaxBurden),
      ''
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Сумма дохода:', formatCsvNumber(results.incomeAmount)],
      ['Период:', formData.incomePeriod === 'month' ? 'Месяц' : formData.incomePeriod === 'quarter' ? 'Квартал' : formData.incomePeriod === 'year' ? 'Год' : 'Произвольный период'],
      ['Налоговая система:', formData.taxSystem === 'npd' ? 'Самозанятость (НПД)' : formData.taxSystem === 'usn_income' ? 'УСН Доходы' : 'УСН Доходы-Расходы'],
      ['Налоговая ставка:', `${results.taxRate}%`],
      ['Налог:', formatCsvNumber(results.calculatedTax)],
      ['Эффективная налоговая нагрузка:', `${results.effectiveTaxRate.toFixed(2)}%`],
      ['Чистый доход:', formatCsvNumber(results.netIncome)],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_налогов_самозанятого.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчёт "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTax();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calculator className="w-6 h-6 text-indigo-500" />
          Параметры расчета налогов
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Налоговый статус и режим налогообложения */}
        <TaxStatusSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          fiscalRoles={fiscalRoles}
          taxSystems={taxSystems}
        />

        {/* Доходы */}
        <IncomeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          periodTypes={periodTypes}
        />

        {/* Расходы */}
        <ExpensesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Вычеты и льготы */}
        <DeductionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          russianRegions={russianRegions}
        />

        {/* Дополнительные опции */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать налоги'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;